// Shared currency catalog for merchant preferences and payment-related forms.
export const SUPPORTED_CURRENCIES = [
  { code: "AED", name: "UAE Dirham", locale: "en-AE", symbol: "د.إ" },
  { code: "ARS", name: "Argentine Peso", locale: "es-AR", symbol: "AR$" },
  { code: "AUD", name: "Australian Dollar", locale: "en-AU", symbol: "A$" },
  { code: "BDT", name: "Bangladeshi Taka", locale: "en-BD", symbol: "৳" },
  { code: "BHD", name: "Bahraini Dinar", locale: "en-BH", symbol: "د.ب" },
  { code: "BRL", name: "Brazilian Real", locale: "pt-BR", symbol: "R$" },
  { code: "CAD", name: "Canadian Dollar", locale: "en-CA", symbol: "C$" },
  { code: "CHF", name: "Swiss Franc", locale: "de-CH", symbol: "CHF" },
  { code: "CLP", name: "Chilean Peso", locale: "es-CL", symbol: "CLP$" },
  { code: "CNY", name: "Chinese Yuan", locale: "zh-CN", symbol: "CN¥" },
  { code: "COP", name: "Colombian Peso", locale: "es-CO", symbol: "COP$" },
  { code: "CZK", name: "Czech Koruna", locale: "cs-CZ", symbol: "Kč" },
  { code: "DKK", name: "Danish Krone", locale: "da-DK", symbol: " kr" },
  { code: "EGP", name: "Egyptian Pound", locale: "en-EG", symbol: "E£" },
  { code: "EUR", name: "Euro", locale: "de-DE", symbol: "€" },
  { code: "GBP", name: "British Pound", locale: "en-GB", symbol: "£" },
  { code: "HKD", name: "Hong Kong Dollar", locale: "en-HK", symbol: "HK$" },
  { code: "HUF", name: "Hungarian Forint", locale: "hu-HU", symbol: "Ft" },
  { code: "IDR", name: "Indonesian Rupiah", locale: "id-ID", symbol: "Rp" },
  { code: "ILS", name: "Israeli New Shekel", locale: "he-IL", symbol: "₪" },
  { code: "INR", name: "Indian Rupee", locale: "en-IN", symbol: "₹" },
  { code: "JPY", name: "Japanese Yen", locale: "ja-JP", symbol: "¥" },
  { code: "KRW", name: "South Korean Won", locale: "ko-KR", symbol: "₩" },
  { code: "KWD", name: "Kuwaiti Dinar", locale: "en-KW", symbol: "د.ك" },
  { code: "MXN", name: "Mexican Peso", locale: "es-MX", symbol: "MX$" },
  { code: "MYR", name: "Malaysian Ringgit", locale: "ms-MY", symbol: "RM" },
  { code: "NGN", name: "Nigerian Naira", locale: "en-NG", symbol: "₦" },
  { code: "NOK", name: "Norwegian Krone", locale: "nb-NO", symbol: " kr" },
  { code: "NZD", name: "New Zealand Dollar", locale: "en-NZ", symbol: "NZ$" },
  { code: "OMR", name: "Omani Rial", locale: "en-OM", symbol: "ر.ع." },
  { code: "PHP", name: "Philippine Peso", locale: "en-PH", symbol: "₱" },
  { code: "PKR", name: "Pakistani Rupee", locale: "en-PK", symbol: "Rs" },
  { code: "PLN", name: "Polish Zloty", locale: "pl-PL", symbol: " zł" },
  { code: "QAR", name: "Qatari Riyal", locale: "en-QA", symbol: "ر.ق." },
  { code: "SAR", name: "Saudi Riyal", locale: "en-SA", symbol: "ر.س" },
  { code: "SEK", name: "Swedish Krona", locale: "sv-SE", symbol: " kr" },
  { code: "SGD", name: "Singapore Dollar", locale: "en-SG", symbol: "S$" },
  { code: "THB", name: "Thai Baht", locale: "th-TH", symbol: "฿" },
  { code: "TRY", name: "Turkish Lira", locale: "tr-TR", symbol: "₺" },
  { code: "TWD", name: "Taiwan Dollar", locale: "zh-TW", symbol: "NT$" },
  { code: "UAH", name: "Ukrainian Hryvnia", locale: "uk-UA", symbol: "₴" },
  { code: "USD", name: "US Dollar", locale: "en-US", symbol: "$" },
  { code: "VND", name: "Vietnamese Dong", locale: "vi-VN", symbol: "₫" },
  { code: "ZAR", name: "South African Rand", locale: "en-ZA", symbol: "R" },
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number]["code"];
// Keep accepting valid Shopify ISO currency codes beyond the curated dropdown list.
export type Currency = SupportedCurrency | (string & {});

const currencyConfig = new Map<string, (typeof SUPPORTED_CURRENCIES)[number]>(
  SUPPORTED_CURRENCIES.map((currency) => [currency.code, currency]),
);

function getCurrencyConfig(currency: Currency) {
  return currencyConfig.get(currency) ?? { locale: "en-US", symbol: `${currency} ` };
}

function parseAmount(amount: number | string): number {
  return typeof amount === "string" ? Number.parseFloat(amount) : amount;
}

export function formatCurrency(amount: number | string, currency: Currency = "USD"): string {
  const config = getCurrencyConfig(currency);
  try {
    return new Intl.NumberFormat(config.locale, { style: "currency", currency }).format(parseAmount(amount));
  } catch {
    return `${currency} ${parseAmount(amount).toFixed(2)}`;
  }
}

export function formatCurrencyCompact(amount: number | string, currency: Currency = "USD"): string {
  const config = getCurrencyConfig(currency);
  const value = parseAmount(amount);
  try {
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency,
      useGrouping: false,
    }).format(value);
  } catch {
    return `${config.symbol}${value.toFixed(2)}`;
  }
}

// Legacy support - defaults to INR for backward compatibility.
export function formatINR(amount: number | string): string {
  return formatCurrency(amount, "INR");
}

export function formatINRCompact(amount: number | string): string {
  return formatCurrencyCompact(amount, "INR");
}

export function getCurrencySymbol(currency: Currency = "USD"): string {
  return getCurrencyConfig(currency).symbol;
}
